import * as React from 'react';

import {
  Box,
  Container,
  CssBaseline,
  Fab,
  LinearProgress,
  List,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery } from '@apollo/client';

import EditClientRateContainer from './EditClientRate';
import ClientRateItem, { ClientRate_ItemFragment } from './ClientRateItem';
import { getDestinationCurrencies } from '../../../util/options';
import FundClientRateContainer from './FundClientRate';
import { graphql, FragmentType, useFragment } from '../../../__generated__';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Copyright from '../../../components/common/Copyright';

export const getTenantRatesQuery = graphql(/* GraphQL */ `
  query Rates($tenantId: String!) {
    rates(tenantId: $tenantId) {
      ...ClientRate_ItemFragment
    }
  }
`);

export const deleteRateMutation = graphql(/* GraphQL */ `
  mutation DeleteRate($tenantId: ID!, $rateId: ID!) {
    deleteRate(tenantId: $tenantId, id: $rateId) {
      code
      message
      success
    }
  }
`);

export type ClientRatesProps = {
  tenantId: string;
  showAddButton: boolean;
  allowEdits: boolean;
  allowDelete: boolean;
};

const ClientRates = (props: ClientRatesProps) => {
  let { tenantId, showAddButton, allowEdits, allowDelete } = props;
  const { data, loading, error, refetch } = useQuery(getTenantRatesQuery, {
    variables: { tenantId: tenantId || '' },
    skip: (tenantId || '')?.length === 0,
  });
  const [deleteRate, { error: deleteError, loading: deleteLoading }] =
    useMutation(deleteRateMutation, {
      refetchQueries: [getTenantRatesQuery],
    });
  const [openAdd, setOpenAdd] = React.useState<boolean>(false);
  const [openFunding, setOpenFunding] = React.useState<boolean>(false);
  const [openEdit, setOpenEdit] = React.useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [selectedRate, setSelectedRate] =
    React.useState<FragmentType<typeof ClientRate_ItemFragment>>();
  const rate = useFragment(ClientRate_ItemFragment, selectedRate) || null;

  const availableDestinations = React.useMemo(() => {
    let destinations = getDestinationCurrencies();
    const rates: { destination: string }[] = (data?.rates || []) as {
      destination: string;
    }[];
    if (rates.length > 0) {
      destinations = destinations.filter(
        (flag) =>
          rates.findIndex((rate) => rate.destination === flag.value) === -1
      );
    }
    return destinations;
  }, [data?.rates]);

  const onCloseEdit = (itemChanged?: boolean) => {
    setOpenEdit(false);
    if (itemChanged) {
      refetch();
    }
  };

  const onCloseFunding = (itemChanged?: boolean) => {
    setOpenFunding(false);
    if (itemChanged) {
      refetch();
    }
  };

  const onOpenAdd = () => {
    setOpenAdd(true);
  };

  const onCloseAdd = (itemChanged?: boolean) => {
    setOpenAdd(false);
    if (itemChanged) {
      refetch();
    }
  };

  const onDelete = () => {
    setConfirmDelete(true);
  };

  const onDeleteRate = async () => {
    setConfirmDelete(false);
    if (rate) {
      deleteRate({ variables: { tenantId: tenantId, rateId: rate.id } });
    }
  };

  const displayError = error?.message || deleteError?.message;
  const dataLoading = loading || deleteLoading;

  if (displayError) {
    return <div>{displayError}</div>;
  }

  if (!tenantId) {
    return <div>Missing platform client id</div>;
  }

  const rates = data?.rates || [];

  return (
    <Container component="main">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {dataLoading && <LinearProgress />}
        {displayError && (
          <Typography variant="caption" gutterBottom color="red">
            {displayError}
          </Typography>
        )}
        {rates.length === 0 && <div>No available rates</div>}
        {rates.length > 0 && (
          <>
            <List
              sx={{ width: '100%', minWidth: 330, bgcolor: 'background.paper' }}
            >
              {rates.map((rate, index) => {
                if (rate === null) return null;
                return (
                  <ClientRateItem
                    key={index}
                    tenant={tenantId || ''}
                    rate={rate}
                    disableEdit={!allowEdits}
                    setSelectedRate={setSelectedRate}
                    onEdit={() => setOpenEdit(true)}
                    onFund={() => setOpenFunding(true)}
                    onDelete={onDelete}
                  />
                );
              })}
            </List>
          </>
        )}
        {showAddButton && availableDestinations.length > 0 && (
          <Fab
            sx={{ position: 'fixed', bottom: 50, right: 50 }}
            aria-label={'Add'}
            color={'primary'}
            onClick={onOpenAdd}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>
      {allowEdits && selectedRate && (
        <FundClientRateContainer
          open={openFunding}
          tenantId={tenantId}
          rate={selectedRate}
          onClose={onCloseFunding}
        />
      )}
      {allowEdits && selectedRate && (
        <EditClientRateContainer
          open={openEdit}
          tenantId={tenantId}
          rate={selectedRate}
          onClose={onCloseEdit}
          destinations={getDestinationCurrencies()}
        />
      )}
      {allowEdits && (
        <EditClientRateContainer
          open={openAdd}
          tenantId={tenantId}
          destinations={availableDestinations}
          onClose={onCloseAdd}
        />
      )}
      {allowDelete && (
        <ConfirmDialog
          open={confirmDelete}
          title={'Deactivate Agent'}
          message={'Are you sure you want to delete this rate?'}
          onCancel={() => setConfirmDelete(false)}
          onOk={onDeleteRate}
        />
      )}
      <Copyright />
    </Container>
  );
};

export default ClientRates;
